import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import TextWinds from '../../../components/common/TextWinds';
import { NormalPage } from '../../../components/layout/page/NormalPage';
import MyLearningCard from './component/MyLearningCard';
import { NormalPageTitle } from '../../../components/layout/page/NormalPageTitle';
import { Button } from '@material-tailwind/react';
import instance from '../../../api/axios/utils.ts/instance';
import { useAppDispatch } from '../../../store/hook';
import {
  closeModal,
  openAlert,
  openConfirm,
  openModal,
} from '../../../store/features/modalSlice';
import { extensionLink } from './extensionLink';
import {
  TermSkeleton,
  TermType,
  useTerm,
} from '../../../components/common/Term';
import { NavCard, NavCardSkeleton } from '../../../components/common/NavCard';
import { useCourseFinderLinks } from '../../../hooks/useCourseFinderLinks';
import HFbutton from '../../../components/button/HFbutton';
import { SITE_CONFIG_KEY } from '../../../constants/types/site-config-type';
import { useSiteConfig } from '../../../hooks/useSiteConfig';

const LEARNING_GUIDE_FALLBACK =
  'https://jebs-notice.oopy.io/58e22264-d335-4b74-aff1-3099f4b01d9e';

const SITE_CONFIG_KEYS = [SITE_CONFIG_KEY.LEARNING_GUIDE];

const LearningMain = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { configs } = useSiteConfig(SITE_CONFIG_KEYS);
  const { links: courseLinks, loading: courseLinksLoading } =
    useCourseFinderLinks();
  const openLearningGuide = () => {
    window.open(
      configs[SITE_CONFIG_KEY.LEARNING_GUIDE] || LEARNING_GUIDE_FALLBACK,
      '_blank',
    );
  };
  const [pageLoaded, setPageLoaded] = useState(false); // 페이지 로드 여부
  const [data, setData] = useState([]);

  useEffect(() => {
    // API 호출 함수
    const fetchData = async () => {
      try {
        const response = await instance.get('/v1/learning');
        const res = response.data;
        setData(res.data);
        setPageLoaded(true);
      } catch (error) {}
    };

    fetchData(); // 데이터 가져오기 함수 호출
  }, []); // 컴포넌트가 처음 렌더링될 때만 실행되도록 []

  const handleStart = (learningId: string) => {
    dispatch(
      openConfirm({
        message: '아직 학습을 시작하지 않았어요.\n학습을 시작할까요?',
        onConfirm: () => {
          openTermConsentModal(learningId);
        },
        confirmText: '시작',
      }),
    );
  };

  const handleExtend = (learningId: string) => {
    const courseId = data.find((item) => item.myLearning.id === learningId)
      .course.id;
    const link = extensionLink(courseId);
    if ('' !== link) {
      window.open(link, '_blank');
    } else {
      dispatch(
        openAlert({
          message: '본 코스는 연장이 불가합니다.\n고객센터로 문의해주세요.',
        }),
      );
    }
  };

  const actionOpenLearning = (learningId: string) => {
    const openId = data.find((item) => item.myLearning.id === learningId)
      .myLearning.openId;

    putOpenLearning(learningId, openId).then((res) => {
      navigate(`/learning/detail/${res.data.myLearning.id}`);
    });
  };

  const putOpenLearning = async (learningId: string, openId: string) => {
    try {
      const response = await instance.put(
        `/v1/learning/${learningId}/open/${openId}`,
      );
      return response.data;
    } catch (error) {}
  };

  /**
   * 내 학습 약관 동의 컨펌창 열기
   * @param learningId
   */
  const openTermConsentModal = (learningId: string) => {
    const courseName = data.find((item) => item.myLearning.id === learningId)
      .course.name;
    const basicList = [
      'JEMM Basic Teacher',
      'JEMM Advanced Teacher',
      'JEMM Kids Teacher',
    ];

    const termType = basicList.includes(courseName)
      ? TermType.COURSE_JEMM
      : TermType.COURSE_GENERAL;

    const learningName = basicList.includes(courseName)
      ? '영어교실 코스'
      : '내 학습';
    dispatch(
      openModal({
        body: (
          <TermConsentModal
            learningId={learningId}
            learningName={learningName}
            termType={termType}
            putOpenLearning={actionOpenLearning}
          />
        ),
      }),
    );
  };

  return (
    pageLoaded && (
      <>
        <NormalPage className="pb-[30px]">
          <NormalPageTitle
            title="내 학습 (교사대학)"
            action={
              <div className="hidden xl:block">
                <HFbutton
                  type="primary-outlined"
                  width="auto"
                  onClick={openLearningGuide}
                >
                  내 학습 (교사대학) 이용 메뉴얼
                </HFbutton>
              </div>
            }
          />
          <section className="xl:bg-skyblue_5">
            <div className="mx-auto flex w-full flex-wrap gap-[22px] pb-[64px] pt-3.5 xl:max-w-page">
              {data.length > 0 ? (
                data.map((item, index) => (
                  <MyLearningCard
                    key={index}
                    data={item.myLearning}
                    linkTo={`/learning/detail/${item.myLearning.id}`}
                    openStart={(id) => handleStart(id)}
                    openExtend={handleExtend}
                  />
                ))
              ) : (
                <div className="flex w-full flex-col">
                  <TextWinds
                    type="title_h2"
                    className="mt-3 w-full text-center"
                  >
                    아직 구매한 코스가 없습니다.
                  </TextWinds>
                  <br />
                  <TextWinds type="title_h2" className="w-full text-center">
                    코스 구매 후 학습을 시작하세요.
                  </TextWinds>

                  {/* 코스 카테고리별 바로가기 카드 목록 */}
                  <div className="mt-5 grid grid-cols-1 gap-4 lg:mt-9 lg:grid-cols-3">
                    {courseLinksLoading
                      ? Array.from({ length: 3 }, (_, i) => (
                          <NavCardSkeleton key={i} />
                        ))
                      : courseLinks.map((item) => (
                          <NavCard
                            key={item.id}
                            to={item.linkUrl}
                            imageSrc={item.iconImageUrl ?? undefined}
                            topText={item.description ?? undefined}
                            bottomText={item.name}
                          />
                        ))}
                  </div>
                </div>
              )}
            </div>
          </section>
          <div className="block xl:hidden">
            <HFbutton type="primary-outlined" onClick={openLearningGuide}>
              내 학습 (교사대학) 이용 메뉴얼
            </HFbutton>
          </div>
        </NormalPage>
      </>
    )
  );
};

/** 내 학습 약관 동의 모달 */
const TermConsentModal = ({
  learningId,
  learningName,
  termType,
  putOpenLearning,
}: {
  learningId: string;
  learningName: string;
  termType: TermType;
  putOpenLearning: (id: string) => void;
}) => {
  const dispatch = useAppDispatch();
  const { content, loading } = useTerm(termType);
  const closeBtnhandler = () => {
    dispatch(closeModal());
  };

  const confirmBtnhandler = () => {
    dispatch(closeModal());
    putOpenLearning(learningId);
  };

  return (
    <div className="m-auto w-[600px] rounded bg-white p-4">
      <TextWinds type="content" className="mb-4">
        {learningName} 시작 시 약관 동의가 필요해요.
      </TextWinds>
      <TextWinds type="title_h3">{termType}</TextWinds>
      <div className="h-auto max-h-[70vh] overflow-y-auto border p-4">
        {loading ? (
          <TermSkeleton />
        ) : (
          <pre className="whitespace-pre-wrap">{content}</pre>
        )}
      </div>
      <div className="grid grid-cols-2 gap-2 pt-4">
        <Button
          onClick={() => closeBtnhandler()}
          className="border border-purple3 bg-white text-purple5 hover:bg-purple3 hover:text-white"
        >
          취소
        </Button>
        {/* 본문 조회 전 동의 방지 */}
        <Button
          disabled={loading}
          onClick={() => confirmBtnhandler()}
          className="bg-purple5 text-white hover:bg-purple6"
        >
          동의
        </Button>
      </div>
    </div>
  );
};

export default LearningMain;
